import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export interface ServiceItem {
  icon: string;
  title: string;
  description: string;
  link?: string;
}

export interface ServicesGridProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  items: ServiceItem[];
  ctaText?: string;
  ctaLink?: string;
}

export default function ServicesGrid({
  eyebrow,
  title,
  subtitle,
  items,
  ctaText,
  ctaLink,
}: ServicesGridProps) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-off-white py-28 lg:py-40">
      <div className="mx-auto px-6 lg:px-12 xl:px-20">
        {/* Section heading */}
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="max-w-xl">
            <div className="flex items-center gap-4 mb-5">
              <span className="block w-8 h-px bg-gold flex-shrink-0" />
              <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                {eyebrow}
              </span>
            </div>
            <h2
              className="font-serif text-charcoal leading-tight"
              style={{ fontSize: 'clamp(2rem, 3.8vw, 3.2rem)', fontWeight: 300 }}
            >
              {title}
            </h2>
          </div>
          <p className="text-stone text-sm leading-[1.85] max-w-sm lg:text-right">
            {subtitle}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100">
          {items.map((item, i) => (
            <ServiceCard key={item.title} item={item} index={i} />
          ))}
        </div>

        {/* Mid-page CTA */}
        {ctaText && ctaLink && (
          <div className="mt-16 text-center">
            <Link
              to={ctaLink}
              className="whitespace-nowrap inline-flex items-center gap-3 bg-charcoal text-white text-[10px] font-bold tracking-[0.25em] uppercase px-10 py-4 hover:bg-charcoal/80 transition-colors duration-300 cursor-pointer group"
            >
              {ctaText}
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function ServiceCard({ item, index }: { item: ServiceItem; index: number }) {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`relative bg-white p-10 group hover:-translate-y-1 transition-all duration-500 cursor-default overflow-hidden ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      {/* Background number */}
      <span
        className="absolute top-2 right-4 font-serif text-charcoal/[0.04] select-none pointer-events-none leading-none"
        style={{ fontSize: '7rem', fontWeight: 700 }}
      >
        {String(index + 1).padStart(2, '0')}
      </span>

      {/* Gold top border that grows on hover */}
      <div className="absolute top-0 left-0 w-0 h-[2px] bg-gold group-hover:w-full transition-all duration-500 ease-luxury" />

      {/* Icon */}
      <div className="w-11 h-11 flex items-center justify-center mb-7 border border-gold/30 group-hover:border-gold group-hover:bg-gold/5 transition-all duration-300">
        <i className={`${item.icon} text-gold text-xl`}></i>
      </div>

      <h3
        className="font-serif text-charcoal mb-4 leading-tight"
        style={{ fontSize: 'clamp(1.3rem, 2vw, 1.7rem)', fontWeight: 500 }}
      >
        {item.title}
      </h3>
      <p className="text-stone text-sm leading-[1.85] mb-7">
        {item.description}
      </p>

      {item.link && (
        <Link
          to={item.link}
          className="inline-flex items-center gap-2 text-gold text-[9px] font-bold tracking-[0.25em] uppercase hover:gap-3 transition-all duration-300 cursor-pointer"
        >
          Learn More
          <i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}
